import { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import useQueryGetMe from '@/api/auth/getMe.ts';
import { AlertStatus, DeviceStatus } from '@/api/user/getUsers.ts';
import { IReqGetUsersBbox, IUserBbox } from '@/api/user/getUsersBbox.ts';
import MarkerWindow from '@/pages/dashboard/MarkerWindow.tsx';
import ClusterMarker from '@/pages/dashboard/ClusterMarker.tsx';
import { getAlertRank, getAlertStatusByRank } from '@/pages/dashboard/dashboardClusters.ts';
import { useDashboardClusters } from '@/pages/dashboard/useDashboardClusters.ts';
import { useStableCallback } from '@/util/useStableCallback.ts';
import UnifiedMap from '@/component/map/unified/UnifiedMap';
import UnifiedCircle from '@/component/map/unified/UnifiedCircle';
import UnifiedInfoWindow from '@/component/map/unified/UnifiedInfoWindow';

import { useMapType } from '@/component/map/MapContext';
import { reverseGeocode } from '@/component/map/unified/UnifiedGeocoder';
import { bd09ToWgs84, wgs84ToBd09 } from '@/util/coordTransform';
import {
    getDashboardMapCenter,
    getDashboardMapZoom,
    setDashboardMapCenter,
    setDashboardMapZoom
} from '@/pages/dashboard/dashboardMapCache';

interface IProps {
    className?: string;
    users?: IUserBbox[];
    onBoundsChange?: (bounds: IReqGetUsersBbox) => void;
}

export enum MarketTYPE {
    CLUSTER,
    SINGLE
}

export const DASHBOARD_MAP_OPTIONS = {
    mapTypeControl: false,
    fullscreenControl: false
}

export interface IMarker<T = IUserBbox> {
    id: number;
    type?: MarketTYPE;
    location?: IMarketLocation;
    alertStatus?: AlertStatus;
    deviceStatus?: DeviceStatus;
    users: T[];
}

export interface IMarketLocation {
    latitude?: number;
    longitude?: number;
    accuracy?: number;
    timestamp?: string;
}

export interface IMarkerInfo<T = IUserBbox> extends IMarker<T> {
    locationName?: string;
    render?: any;
    address: string;
}

const getMarkerColor = (rank: number) => {
    switch (getAlertStatusByRank(rank)) {
        case AlertStatus.EMERGENCY:
            return '#FF003D';
        case AlertStatus.BIO:
            return '#FF7D33';
        default:
            return '#2966F4';
    }
};

const DashboardMapDefault = forwardRef(({ className, users, onBoundsChange }: IProps, ref: ForwardedRef<any>) => {
    const [map, setMap] = useState<any>(null);
    const [userInfo, setUserInfo] = useState<IMarkerInfo<IUserBbox>>();
    const [selectedMarker, setSelectedMarker] = useState<IMarker<IUserBbox> | null>(null);
    const [zoomLevel, setZoomLevel] = useState(getDashboardMapZoom);
    const { data: me } = useQueryGetMe();
    const { mapType } = useMapType();
    const initialCenter = useMemo(
        () => getDashboardMapCenter(me?.latitude, me?.longitude),
        [me?.latitude, me?.longitude]
    );
    const initialZoom = useMemo(() => getDashboardMapZoom(), []);

    // Baidu 맵 초기화 후 bounds를 즉시 전달
    const getBaiduBoundsAsWgs84 = () => {
        if (!map) return null;
        const mapBounds = map.getBounds();
        if (!mapBounds) return null;
        const sw = mapBounds.getSouthWest();
        const ne = mapBounds.getNorthEast();
        const [swLat, swLng] = bd09ToWgs84(sw.lat, sw.lng);
        const [neLat, neLng] = bd09ToWgs84(ne.lat, ne.lng);
        return { startLat: swLat, endLat: neLat, startLng: swLng, endLng: neLng };
    };

    // Baidu 맵 초기화 후 bounds를 즉시 전달
    useEffect(() => {
        if (!map || mapType !== 'baidu') return;
        const timer = setTimeout(() => {
            const bounds = getBaiduBoundsAsWgs84();
            if (bounds && onBoundsChange) {
                onBoundsChange(bounds);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [map, mapType]);

    const mapControls = {
        setLocation(latitude: number, longitude: number, zoom?: number) {
            if (
                typeof latitude === 'number' &&
                typeof longitude === 'number' &&
                !isNaN(latitude) &&
                !isNaN(longitude) &&
                isFinite(latitude) &&
                isFinite(longitude)
            ) {
                if (mapType === 'google' && map) {
                    map.panTo({ lat: latitude, lng: longitude });
                    if (zoom && typeof zoom === 'number' && !isNaN(zoom) && isFinite(zoom)) {
                        map.setZoom(zoom);
                    }
                } else if (mapType === 'baidu' && map) {
                    const [bdLat, bdLng] = wgs84ToBd09(latitude, longitude);
                    const point = new window.BMapGL.Point(bdLng, bdLat);
                    map.panTo(point);
                    if (zoom) map.setZoom(zoom);
                }
            } else {
                console.warn('Invalid coordinates provided to setLocation:', { latitude, longitude, zoom });
            }
        }
    };

    useImperativeHandle(ref, () => mapControls);

    const handleIdle = () => {
        if (!map) return;

        if (mapType === 'google') {
            const mapBounds = map.getBounds();
            if (mapBounds && onBoundsChange) {
                const ne = mapBounds.getNorthEast();
                const sw = mapBounds.getSouthWest();
                onBoundsChange({
                    startLat: sw.lat(),
                    endLat: ne.lat(),
                    startLng: sw.lng(),
                    endLng: ne.lng()
                });
            }
        } else if (mapType === 'baidu') {
            const bounds = getBaiduBoundsAsWgs84();
            if (bounds && onBoundsChange) {
                onBoundsChange(bounds);
            }
        }
    };

    const openUserInfo = async (info?: IMarker<IUserBbox>) => {
        if (!info || userInfo?.id === info?.id) {
            setUserInfo(undefined);
            setSelectedMarker(null);
        } else {
            const { latitude = 0, longitude = 0 } = info?.location || {};

            if (latitude === 0 && longitude === 0) {
                const infoWithoutAddress: IMarkerInfo<IUserBbox> = {
                    ...info!,
                    address: 'Coordinates not available',
                };
                setUserInfo(infoWithoutAddress);
                setSelectedMarker(info);
                return;
            }

            const address = await reverseGeocode(mapType, latitude, longitude);
            const infoWithAddress: IMarkerInfo<IUserBbox> = {
                ...info!,
                address
            };
            setUserInfo(infoWithAddress);
            setSelectedMarker(info);
        }
    };


    const { markers, selectMarker } = useDashboardClusters(users, zoomLevel, getAlertRank, true);

    const handleMarkerClick = useStableCallback((key: string) => {
        const selection = selectMarker(key);
        if (!selection) return;

        const { marker, users: members, zoomTo } = selection;
        if (zoomTo) {
            mapControls.setLocation(marker.lat, marker.lng, zoomTo);
            return;
        }

        const { user } = marker;
        openUserInfo({
            id: user?.id ?? members[0]?.id,
            type: user ? MarketTYPE.SINGLE : MarketTYPE.CLUSTER,
            alertStatus: getAlertStatusByRank(marker.rank),
            location: {
                latitude: marker.lat,
                longitude: marker.lng,
                accuracy: user?.location?.accuracy,
                timestamp: user?.location?.timestamp
            },
            users: members
        });
    });

    return (
        <div className={`${className} relative`}>
            <div style={{ width: '100%', height: '100%' }}>
                {me && (
                    <UnifiedMap
                        onLoad={(mapInstance) => setMap(mapInstance)}
                        center={initialCenter}
                        zoom={initialZoom}
                        onZoomChanged={() => {
                            if (mapType === 'google' && map) {
                                setZoomLevel(map.getZoom() || 18);
                                setDashboardMapZoom(map.getZoom());
                            } else if (mapType === 'baidu' && map) {
                                setZoomLevel(map.getZoom() || 18);
                                setDashboardMapZoom(map.getZoom());
                            }
                        }}
                        onCenterChanged={() => {
                            if (mapType === 'google' && map) {
                                const center = map.getCenter();
                                if (center) {
                                    setDashboardMapCenter(center.lat(), center.lng());
                                }
                            } else if (mapType === 'baidu' && map) {
                                const center = map.getCenter();
                                if (center) {
                                    const [wgsLat, wgsLng] = bd09ToWgs84(center.lat, center.lng);
                                    setDashboardMapCenter(wgsLat, wgsLng);
                                }
                            }
                        }}
                        onIdle={handleIdle}
                        mapContainerStyle={{ width: '100%', height: '100%' }}
                        options={DASHBOARD_MAP_OPTIONS}
                    >
                        {markers.map((marker) => (
                            <ClusterMarker
                                key={marker.key}
                                markerKey={marker.key}
                                lat={marker.lat}
                                lng={marker.lng}
                                count={marker.count}
                                color={getMarkerColor(marker.rank)}
                                onSelect={handleMarkerClick}
                            />
                        ))}

                        {selectedMarker && (
                            <UnifiedCircle
                                center={{ lat: selectedMarker.location?.latitude || 0, lng: selectedMarker.location?.longitude || 0 }}
                                radius={selectedMarker.location?.accuracy || 100}
                                options={{
                                    fillColor: '#78bfffcc',
                                    strokeColor: '#78bfffcc',
                                    strokeWeight: 1,
                                }}
                            />
                        )}
                        {userInfo && (
                            <UnifiedInfoWindow
                                position={{ lat: userInfo.location?.latitude || 0, lng: userInfo.location?.longitude || 0 }}
                                onCloseClick={() => {
                                    setUserInfo(undefined);
                                    setSelectedMarker(null);
                                }}
                            >
                                <div style={{ fontFamily: "'Noto Sans KR', sans-serif", fontWeight: 400 }}>
                                    <MarkerWindow info={userInfo} isMasking={me?.email === 'dbd_cm@clomental.com'} />
                                </div>
                            </UnifiedInfoWindow>
                        )}
                    </UnifiedMap>
                )}
            </div>
        </div>
    );
});

export default DashboardMapDefault;
